import React, { useState, useEffect, useCallback, useRef } from 'react';
// import { Clipboard, Home, Layers } from 'lucide-react'; // Icons not used directly in App.tsx anymore
import FloorPlanCreator from './components/FloorPlanCreator';
import EstateFloorPlan from './components/Estate_floorplan';
import PropertyInventory from './components/PropertyInventory';
import LoadingScreen from './components/LoadingScreen';
import HomeScreen from './components/HomeScreen';
import SettingsPage from './components/SettingsPage';
import ChatPage from './components/ChatPage';
import TeamChatWithSidebar from './components/TeamChatWithSidebar';
import ChatHubPage from './components/ChatHubPage';
import MyFilesPage from './components/MyFilesPage';
import AuthPage from './components/AuthPage';
import RetrofitPage from './components/RetrofitPage';
import EstateAgencyPage from './components/EstateAgencyPage';
import CalendarPage from './components/CalendarPage';
import TeamManagementPage from './components/TeamManagementPage';
import InviteAcceptPage from './components/InviteAcceptPage';
import LandingPage from './components/LandingPage';
import ChatNotificationPopup from './components/ChatNotificationPopup';
import SubscriptionExpiredModal from './components/SubscriptionExpiredModal';
import FAQPage from './components/FAQPage';
import ContactPage from './components/ContactPage';
import FloorPlansFeaturePage from './components/features/FloorPlansFeaturePage';
import PropertyInventoryFeaturePage from './components/features/PropertyInventoryFeaturePage';
import AIChatFeaturePage from './components/features/AIChatFeaturePage';
import EPCPage from './components/forms/EPCPage';
import FloorPlanGraphForm from './components/forms/FloorPlanGraphForm';
import AssessorSiteNotes from './components/forms/AssessorSiteNotes';
import CwiEwiPlan from './components/forms/CwiEwiPlan';
import IwiPlan from './components/forms/IwiPlan';
import RirPlan from './components/forms/RirPlan';
import RetrofitSurveyLayout from './components/layout/RetrofitSurveyLayout';
import { supabase } from './lib/supabaseClient';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp, AppState } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { useAppLifecycle } from './hooks/useAppLifecycle';
import { useDeepLinking } from './hooks/useDeepLinking';
import type { User } from '@supabase/supabase-js';
import { getUserRole } from './services/teamService';
import type { UserRole } from './types/team';
import PushNotificationService from './services/pushNotificationService';
import SubscriptionService from './services/subscriptionService';
import type { UserSubscription } from './services/subscriptionService';
import { sessionManagementService } from './services/sessionManagementService';

export type CurrentPage =
  | { page: 'loading' }
  | { page: 'landing' }
  | { page: 'faq' }
  | { page: 'contact' }
  | { page: 'features/floor-plans' }
  | { page: 'features/property-inventory' }
  | { page: 'features/retrofit-assessments' }
  | { page: 'features/team-collaboration' }
  | { page: 'features/ai-chat' }
  | { page: 'auth'; inviteToken?: string }
  | { page: 'start' }
  | { page: 'floorPlan'; floorPlanId?: string }
  | { page: 'propertyInventory'; inventoryId?: string }
  | { page: 'estateAgency' }
  | { page: 'calendar' }
  | { page: 'myFiles' }
  | { page: 'settings' }
  | { page: 'chatHub' }
  | { page: 'chat' }
  | { page: 'teamChat' }
  | { page: 'retrofit' }
  | { page: 'retrofit/floorplan'; floorPlanId?: string }
  | { page: 'retrofit/epc'; floorPlanId?: string }
  | { page: 'retrofit/floor_plan_graph'; floorPlanId?: string }
  | { page: 'retrofit/site_notes'; floorPlanId?: string }
  | { page: 'retrofit/cwi_ewi'; floorPlanId?: string }
  | { page: 'retrofit/iwi'; floorPlanId?: string }
  | { page: 'retrofit/rir'; floorPlanId?: string }
  | { page: 'retrofit/survey'; floorPlanId?: string }
  | { page: 'team-management' }
  | { page: 'invite'; token?: string };

// Constants
const MIN_LOADING_TIME = 1500; // Minimum time for the loading screen in milliseconds

function App() {
  // console.log('[DEBUG] App: Function component starting');
  
  // Disable browser scroll restoration to prevent scroll position being restored
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const previous = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      console.log('🚫 Browser scroll restoration disabled:', previous, '→ manual');
    } else {
      console.log('⚠️ Browser does not support scroll restoration API');
    }
  }, []);
  
  const [loadingProgress, setLoadingProgress] = useState(0);
  // Skip loading screen for web browsers, only show on native apps
  // Default to auth page (landing is on noorcad.com)
  const [currentPage, setCurrentPage] = useState<CurrentPage>(
    Capacitor.isNativePlatform() ? { page: 'loading' } : { page: 'auth' }
  );
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userSubscription, setUserSubscription] = useState<UserSubscription | null>(null);
  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [hasCompletedInitialVerification, setHasCompletedInitialVerification] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(
    !Capacitor.isNativePlatform() // Web browser loads instantly
  );
  const lastVerificationRef = useRef<Date | null>(null);
  const verificationDebounceRef = useRef<NodeJS.Timeout | null>(null);
  
  // App lifecycle management to prevent session timeout
  const appLifecycle = useAppLifecycle();
  
  // Deep linking for team invitations
  useDeepLinking({
    onInvite: (token) => {
      console.log('[DeepLink] Invitation token received, navigating to auth page');
      navigateToPage({ page: 'auth', inviteToken: token });
    }
  });
  
  // Fetch user role and subscription for route protection
  useEffect(() => {
    const loadUserData = async (isInitialCheck: boolean = false) => {
      if (currentUser) {
        // Rate limiting: Don't verify more than once per 10 seconds (unless initial check)
        const now = new Date();
        if (!isInitialCheck && lastVerificationRef.current && 
            (now.getTime() - lastVerificationRef.current.getTime()) < 10000) {
          console.log('[UserData] Verification rate limited, skipping');
          return;
        }
        
        // Only show blocking UI on FIRST EVER verification
        // All subsequent checks (resume, token refresh, etc.) happen silently in background
        // This prevents pages from hanging while waiting for verification
        const shouldShowBlockingUI = isInitialCheck && !hasCompletedInitialVerification;
        
        if (shouldShowBlockingUI) {
          setIsCheckingSubscription(true);
          console.log('[UserData] Blocking verification (first load only)');
        } else {
          console.log('[UserData] Silent background verification (non-blocking)');
        }
        
        try {
          // Load user role
          const role = await getUserRole();
          setUserRole(role);
          console.log('[UserRole] Loaded user role:', role);

          // Load and check subscription
          const subscriptionService = SubscriptionService.getInstance();
          // Force refresh on initial check, use cache otherwise for speed
          const subscription = await subscriptionService.getUserSubscription(isInitialCheck);
          setUserSubscription(subscription);

          // Check if subscription is expired
          if (subscription?.subscription_end_date) {
            const endDate = new Date(subscription.subscription_end_date);
            const now = new Date();
            const expired = endDate < now;
            setIsSubscriptionExpired(expired);
            
            if (expired) {
              console.log('[Subscription] ⚠️ Subscription expired on:', endDate.toISOString());
            } else {
              console.log('[Subscription] ✅ Active until:', endDate.toISOString());
            }
          } else {
            // No end date means active subscription (standard/pro/business)
            setIsSubscriptionExpired(false);
            console.log('[Subscription] ✅ Active subscription (no expiry)');
          }
          
          // Mark initial verification as complete after first successful check
          if (!hasCompletedInitialVerification) {
            setHasCompletedInitialVerification(true);
            console.log('[UserData] ✅ Initial verification complete - future checks will be silent');
          }
          
          // Update last verification timestamp
          lastVerificationRef.current = now;
        } catch (error) {
          console.error('[UserData] Error loading user data:', error);
          // On error, allow access (fail open)
          setIsSubscriptionExpired(false);
        } finally {
          if (shouldShowBlockingUI) {
            setIsCheckingSubscription(false);
          }
        }
      } else {
        setUserRole(null);
        setUserSubscription(null);
        setIsSubscriptionExpired(false);
        setIsCheckingSubscription(false);
        setHasCompletedInitialVerification(false);
      }
    };

    // Clear any pending debounced verification
    if (verificationDebounceRef.current) {
      clearTimeout(verificationDebounceRef.current);
    }
    
    // Determine if this is the initial check (first time currentUser is set)
    const isInitialCheck = currentUser !== null && !hasCompletedInitialVerification;
    
    // For non-initial checks, debounce to prevent rapid re-verification
    if (!isInitialCheck && currentUser) {
      verificationDebounceRef.current = setTimeout(() => {
        loadUserData(false);
      }, 500); // 500ms debounce
    } else {
      loadUserData(isInitialCheck);
    }
    
    return () => {
      if (verificationDebounceRef.current) {
        clearTimeout(verificationDebounceRef.current);
      }
    };
  }, [currentUser, hasCompletedInitialVerification]);

  // Initialize push notifications when user is authenticated
  useEffect(() => {
    const initPushNotifications = async () => {
      if (currentUser && Capacitor.isNativePlatform()) {
        try {
          console.log('[App] Initializing push notifications for user:', currentUser.id);
          const pushService = PushNotificationService.getInstance();
          await pushService.initialize();
          console.log('[App] ✅ Push notifications initialized');
        } catch (error) {
          console.error('[App] ❌ Failed to initialize push notifications:', error);
        }
      } else if (!currentUser) {
        // Unregister when user logs out
        try {
          const pushService = PushNotificationService.getInstance();
          await pushService.unregister();
          console.log('[App] Push notifications unregistered');
        } catch (error) {
          console.error('[App] Error unregistering push notifications:', error);
        }
      }
    };

    initPushNotifications();
  }, [currentUser]);
  
  console.log('[DEBUG] App: State initialized', { 
    currentPage: currentPage.page, 
    isInitialLoadComplete, 
    loadingProgress,
    currentUser: currentUser?.id || 'null',
    appIsActive: appLifecycle.state.isActive
  });

  const profileAndSessionFetchedRef = useRef(false);
  const minLoadingTimeElapsedRef = useRef(false);
  const currentPageRef = useRef<CurrentPage>(currentPage);

  // Keep the ref updated with the current page
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // Type guard function to ensure proper typing
  const getCurrentPageSafe = (): CurrentPage => {
    return currentPage as CurrentPage;
  };



  const navigateToPage = useCallback((targetPage: CurrentPage, associatedId?: string) => {
    let targetPageWithId: CurrentPage = { ...targetPage };

    if (associatedId) {
      if ('floorPlanId' in targetPageWithId) {
        targetPageWithId = { ...targetPageWithId, floorPlanId: associatedId };
      } else if ('inventoryId' in targetPageWithId) {
        targetPageWithId = { ...targetPageWithId, inventoryId: associatedId };
      }
    }

    // Use ref to avoid circular dependency
    const currentPageValue = currentPageRef.current as CurrentPage;
    const isSamePageType = targetPageWithId.page === currentPageValue.page;
    let isEffectivelySamePage = false;

    if (isSamePageType) {
        if (associatedId) {
            let currentId: string | undefined;
            if ('floorPlanId' in currentPageValue) {
                currentId = currentPageValue.floorPlanId;
            } else if ('inventoryId' in currentPageValue) {
                currentId = currentPageValue.inventoryId;
            }
            if (associatedId === currentId) {
                isEffectivelySamePage = true;
            }
        } else if (!('floorPlanId' in targetPageWithId || 'inventoryId' in targetPageWithId)) {
            const pageDefinitionNeedsId = ['floorPlan', 'propertyInventory', 'retrofit/epc', 'retrofit/floor_plan_graph', 'retrofit/site_notes', 'retrofit/cwi_ewi', 'retrofit/iwi', 'retrofit/rir', 'retrofit/survey'];
            if (!pageDefinitionNeedsId.includes(targetPageWithId.page)) {
            isEffectivelySamePage = true;
            }
        }
    }
    
    if (isEffectivelySamePage) {
      console.log("Navigate: No change in page (or only ID matches).", { targetPage: targetPageWithId, currentPage: currentPageValue, associatedId });
      return;
    }

    console.log("Navigate: Changing page. From:", currentPageValue, "To:", targetPageWithId);
    
    // Immediately scroll to top before page change for instant reset
    console.log("🔝 Scrolling to top before page change");
    
    // Scroll the actual scrolling container (#root) 
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.scrollTop = 0;
      console.log("🔝 #root scroll position after reset:", rootElement.scrollTop);
    }
    
    // Also try window (for compatibility)
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Also scroll after render to catch any restoration
    requestAnimationFrame(() => {
      if (rootElement) {
        rootElement.scrollTop = 0;
      }
      window.scrollTo(0, 0);
      console.log("🔝 Post-render #root scroll:", rootElement?.scrollTop);
    });
    
    setCurrentPage(targetPageWithId);
  }, []);

  const tryFinalizeLoading = useCallback(() => {
    console.log(
      `[tryFinalizeLoading] Checking: minTime=${minLoadingTimeElapsedRef.current}, sessionFetched=${profileAndSessionFetchedRef.current}, isInitialLoadCompleteState=${isInitialLoadComplete}`
    );
    if (minLoadingTimeElapsedRef.current && profileAndSessionFetchedRef.current) {
      if (!isInitialLoadComplete) {
        console.log("[tryFinalizeLoading] Conditions met. Setting isInitialLoadComplete = true.");
        setIsInitialLoadComplete(true);
      } else {
        console.log("[tryFinalizeLoading] Initial load already complete (state).");
      }
    }
  }, [isInitialLoadComplete]);

  // Effect for minimum loading time and progress bar
  useEffect(() => {
    if (isInitialLoadComplete) return;

    console.log("[MinLoadingTimeEffect] Starting minimum loading time countdown and progress.");
    setLoadingProgress(0);
    minLoadingTimeElapsedRef.current = false;

    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        const next = Math.min(prev + 100 / (MIN_LOADING_TIME / 100), 100);
        return next;
      });
    }, 100);

    const timeoutId = setTimeout(() => {
      console.log("[MinLoadingTimeEffect] Minimum loading time has elapsed.");
      minLoadingTimeElapsedRef.current = true;
      tryFinalizeLoading();
    }, MIN_LOADING_TIME);

    return () => {
      console.log("[MinLoadingTimeEffect] Cleanup.");
      clearInterval(progressInterval);
      clearTimeout(timeoutId);
    };
  }, [isInitialLoadComplete, tryFinalizeLoading]);

  // Effect to receive session tokens from cross-domain redirect (noorcad.com → noortech.io)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const inviteToken = params.get('invite');
    
    if (accessToken && refreshToken) {
      console.log('[CrossDomainAuth] Received session tokens from noorcad.com');
      
      // Set the session on this domain
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken
      }).then(({ data, error }) => {
        if (error) {
          console.error('[CrossDomainAuth] Failed to set session:', error);
        } else if (data.session) {
          console.log('[CrossDomainAuth] Session set successfully for user:', data.session.user.id);
          setCurrentUser(data.session.user);
          
          // Clean up URL params
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, '', cleanUrl);
          
          // If there's an invite token, navigate to auth page with it
          if (inviteToken) {
            console.log('[CrossDomainAuth] Invite token detected, navigating to auth page');
            navigateToPage({ page: 'auth', inviteToken });
          } else {
            // Navigate to start page
            console.log('[CrossDomainAuth] Navigating to start page');
            navigateToPage({ page: 'start' });
          }
        }
      }).catch((err) => {
        console.error('[CrossDomainAuth] Unexpected error setting session:', err);
      });
    }
  }, []);

  // Effect for fetching initial session and setting up auth listener
  useEffect(() => {
    // Set flag immediately to prevent loading screen from getting stuck
    if (!profileAndSessionFetchedRef.current) {
      profileAndSessionFetchedRef.current = true;
      console.log("[AuthEffect] Setting profileAndSessionFetched=true at start to unblock loading");
      tryFinalizeLoading();
    }
    
    const performInitialSessionCheck = async () => {
      console.log("[AuthEffect] Performing initial session check...");
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.error("[AuthEffect] Error fetching initial session:", sessionError.message);
        }
        setCurrentUser(session?.user ?? null);
        console.log(`[AuthEffect] Initial session check: user ${session?.user ? session.user.id : 'null'}`);
        
        // If user is already logged in, setup session monitoring immediately (non-blocking)
        if (session?.user) {
          console.log('[AuthEffect] User already logged in - setting up session monitoring (non-blocking)');
          sessionManagementService.setupSessionMonitoring()
            .then(() => console.log('[AuthEffect] ✅ Initial session monitoring active'))
            .catch((err) => console.error('[AuthEffect] Initial session monitoring setup failed:', err));
        }
      } catch (error) {
        console.error("[AuthEffect] Unexpected error fetching initial session:", error);
        setCurrentUser(null);
      }
    };

    performInitialSessionCheck();

    const { data: authSubscription } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log(`[AuthEffect] Auth event: ${event}. Session: ${session ? 'exists' : 'null'}. isInitialLoadCompleteState (at event time): ${isInitialLoadComplete}. AppActive: ${appLifecycle.state.isActive}`);
        
        // Setup real-time session monitoring on login (non-blocking)
        if (event === 'SIGNED_IN' && session) {
          console.log('[AuthEffect] Setting up real-time session monitoring (non-blocking)');
          sessionManagementService.setupSessionMonitoring()
            .then(() => console.log('[AuthEffect] ✅ Session monitoring active'))
            .catch((err) => console.error('[AuthEffect] Session monitoring setup failed:', err));
        }
        
        // Stop monitoring on logout and reset state
        if (event === 'SIGNED_OUT') {
          console.log('[AuthEffect] Stopping session monitoring');
          sessionManagementService.stopSessionMonitoring()
            .catch((err) => console.error('[AuthEffect] Failed to stop monitoring:', err));
          
          // Reset flag so next login goes through full initialization
          profileAndSessionFetchedRef.current = false;
          console.log('[AuthEffect] Reset profileAndSessionFetched flag for next login');
        }
        
        // Don't trigger user state change on TOKEN_REFRESHED if user hasn't changed
        // This prevents the "Verifying account..." screen on every resume
        if (event === 'TOKEN_REFRESHED') {
          if (session?.user?.id === currentUser?.id) {
            console.log('[AuthEffect] TOKEN_REFRESHED for same user - skipping verification trigger');
            return; // Don't update state, avoid re-triggering verification
          }
        }
        
        setCurrentUser(session?.user ?? null);
        
        // Handle session expiration more gracefully
        if (event === 'SIGNED_OUT' && session === null) {
          // Only redirect to auth if this wasn't caused by app backgrounding
          if (appLifecycle.state.isActive) {
            console.log("[AuthEffect] Session expired while app is active - redirecting to auth");
          } else {
            console.log("[AuthEffect] Session expired while app is in background - will try to refresh when app becomes active");
            return; // Don't redirect immediately if app is backgrounded
          }
        }
        
        if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
           if (!profileAndSessionFetchedRef.current) {
                profileAndSessionFetchedRef.current = true;
                console.log(`[AuthEffect] Auth event '${event}' also setting profileAndSessionFetched=true.`);
            }
        }
        
        tryFinalizeLoading();
      }
    );

    return () => {
      console.log("[AuthEffect] Cleaning up auth subscription.");
      authSubscription.subscription.unsubscribe();
    };
  }, [isInitialLoadComplete, tryFinalizeLoading, appLifecycle.state.isActive]);

  // Effect to handle navigation once initial load is complete (now main RoutingEffect)
  useEffect(() => {
    if (isInitialLoadComplete) {
      const currentPageValue = getCurrentPageSafe();
      console.log(`[RoutingEffect] Active. CurrentPage: ${currentPageValue.page}, User: ${currentUser?.id || 'null'}, AppActive: ${appLifecycle.state.isActive}`);

      if (currentUser) { // User is authenticated
        // Don't redirect if we're creating an account (ignore SIGNED_IN during signup)
        const isCreatingAccount = sessionStorage.getItem('creatingAccount') === 'true';
        
        // Route protection for team_admin - only allow calendar, teamChat, chatHub, settings, and start
        if (userRole === 'team_admin') {
          const allowedPages = ['start', 'calendar', 'teamChat', 'chatHub', 'settings', 'auth', 'loading'];
          if (!allowedPages.includes(currentPageValue.page)) {
            console.log(`[RoutingEffect] team_admin trying to access restricted page: ${currentPageValue.page}. Redirecting to start.`);
            navigateToPage({ page: 'start' });
            return;
          }
        }
        
        // Only redirect from auth or loading pages - don't interfere with user on valid pages
        if (currentPageValue.page === 'auth' || currentPageValue.page === 'loading') {
          if (isCreatingAccount) {
            console.log(`[RoutingEffect] User is ON ${currentPageValue.page} but creating account - STAYING on auth page`);
          } else {
            console.log(`[RoutingEffect] User is ON ${currentPageValue.page}. Navigating to 'start'.`);
            navigateToPage({ page: 'start' });
          }
        } else {
          // User is on a valid page (settings, start, etc.) - don't redirect
          console.log(`[RoutingEffect] User authenticated on valid page: ${currentPageValue.page}, no redirect needed`);
        }
      } else { // No user / user is null
        // Only redirect to auth if app is active to prevent unnecessary redirects during backgrounding
        if (appLifecycle.state.isActive) {
          // All users go to auth (landing page is on noorcad.com)
          const targetPage = 'auth';
          
          if (currentPageValue.page !== 'auth' && currentPageValue.page !== 'landing' && currentPageValue.page !== 'faq' && currentPageValue.page !== 'contact' && currentPageValue.page !== 'loading' && currentPageValue.page !== 'invite') {
            console.log(`[RoutingEffect] No user, and ON ${currentPageValue.page}. Navigating to '${targetPage}'.`);
            navigateToPage({ page: targetPage });
          } else if (currentPageValue.page === 'loading') { // Specifically from loading
            console.log(`[RoutingEffect] No user, and ON loading. Navigating to '${targetPage}'.`);
            navigateToPage({ page: targetPage });
          }
        } else {
          console.log(`[RoutingEffect] No user but app is backgrounded - deferring redirect`);
        }
      }
      // Hide splash screen once initial load is done and routing logic has a chance to run
      SplashScreen.hide().catch(err => console.error("Failed to hide splash screen in RoutingEffect", err));
    }
  }, [isInitialLoadComplete, currentUser, currentPage, appLifecycle.state.isActive, userRole]);

  const fetchInitialData = useCallback(async () => {
     console.log("Running fetchInitialData triggered by login/logout - (This function might be redundant if performInitialFetch is primary)");
  }, []);

  const handleLogout = async () => {
    console.log("Attempting to log out...");
    
    try {
      // IMPORTANT: Wait for session removal to complete before signout
      // This ensures the DB session is deleted BEFORE auth check runs again
      console.log('[Logout] Step 1: Removing session from DB...');
      const removeSessionPromise = sessionManagementService.removeSession();
      const sessionTimeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Session removal timeout')), 5000)
      );
      
      await Promise.race([removeSessionPromise, sessionTimeout]).catch(err => {
        console.warn('[Logout] Session removal timeout or error, continuing anyway:', err);
      });
      
      console.log('[Logout] Step 2: Signing out from Supabase...');
      // Sign out with timeout
      const signOutPromise = supabase.auth.signOut();
      const signOutTimeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('SignOut timeout')), 2000)
      );
      
      const { error } = await Promise.race([signOutPromise, signOutTimeout]).catch(err => {
        console.warn('[Logout] SignOut timeout - forcing logout:', err);
        return { error: err };
      }) as { error?: Error };
      
      if (error) {
        console.warn('[Logout] SignOut had issues but forcing logout anyway');
      }
      
      // Force logout immediately
      console.log('[Logout] Step 3: Clearing user state and navigating to auth');
      setCurrentUser(null);
      console.log("Logout successful. Current page before potential nav:", getCurrentPageSafe().page);
      if (isInitialLoadComplete) {
        navigateToPage({ page: 'auth' });
      }
    } catch (err) {
      console.error('[Logout] Unexpected error:', err);
      // Force logout UI anyway
      setCurrentUser(null);
      if (isInitialLoadComplete) {
        navigateToPage({ page: 'auth' });
      }
    }
  };

  // Effect to handle app lifecycle changes for session management
  useEffect(() => {
    const handleAppForeground = async () => {
      console.log("[AppLifecycle] App came to foreground - checking session");
      
      // If user was logged in but session might have expired, try to refresh
      if (currentUser && !appLifecycle.isRefreshing) {
        try {
          await appLifecycle.refreshSession();
          console.log("[AppLifecycle] Session refreshed successfully on foreground");
        } catch (error) {
          console.error("[AppLifecycle] Failed to refresh session on foreground:", error);
          // If refresh fails, the auth state change listener will handle the logout
        }
      }
    };

    if (appLifecycle.state.isActive && appLifecycle.state.lastActiveTime) {
      handleAppForeground();
    }
  }, [appLifecycle.state.isActive, currentUser, appLifecycle.refreshSession, appLifecycle.isRefreshing]);

  const renderCurrentPage = () => {
    const currentPageValue = getCurrentPageSafe();
    console.log('[DEBUG] renderCurrentPage called', { 
      isInitialLoadComplete, 
      currentPage: currentPageValue.page,
      loadingProgress 
    });
    
    if (!isInitialLoadComplete) {
      console.log('[DEBUG] Rendering LoadingScreen with progress:', loadingProgress);
      return <LoadingScreen progress={loadingProgress} />;
    }
    
    console.log('[DEBUG] Rendering page:', currentPageValue.page);

    switch (currentPageValue.page) {
      case 'loading':
        return <LoadingScreen progress={loadingProgress} />;
      case 'landing':
      case 'faq':
      case 'contact':
      case 'features/floor-plans':
      case 'features/property-inventory':
      case 'features/ai-chat':
        // These pages are now on noorcad.com - redirect to auth
        console.warn('[App] User tried to access marketing page on noortech.io - redirecting to auth');
        navigateToPage({ page: 'auth' });
        return <LoadingScreen progress={100} />;
      case 'auth':
        return <AuthPage onNavigate={navigateToPage} inviteToken={(currentPageValue as { page: 'auth'; inviteToken?: string }).inviteToken} />;
      case 'start':
        return <HomeScreen currentUser={currentUser} onNavigate={navigateToPage} />;
      case 'floorPlan':
        return <FloorPlanCreator onNavigate={navigateToPage} floorPlanId={(currentPageValue as { page: 'floorPlan'; floorPlanId?: string }).floorPlanId} currentUser={currentUser} planType="energy" />;
      case 'propertyInventory':
        return <PropertyInventory onNavigate={navigateToPage} inventoryId={(currentPageValue as { page: 'propertyInventory'; inventoryId?: string }).inventoryId} />;
      case 'estateAgency':
        return <EstateAgencyPage onNavigate={navigateToPage} />;
      case 'calendar':
        return <CalendarPage onNavigate={navigateToPage} />;
      case 'settings':
        return <SettingsPage handleLogout={handleLogout} onNavigate={navigateToPage} currentUser={currentUser} />;
      case 'chatHub':
        return <ChatHubPage onNavigate={navigateToPage} />;
      case 'chat':
        return <ChatPage onNavigate={navigateToPage} currentUser={currentUser} />;
      case 'teamChat':
        return <TeamChatWithSidebar onNavigate={navigateToPage} />;
      case 'myFiles':
        return <MyFilesPage onNavigate={navigateToPage} />;
      case 'retrofit':
        return <RetrofitPage onNavigate={navigateToPage} />;
      case 'retrofit/floorplan':
        return <EstateFloorPlan onNavigate={navigateToPage} floorPlanId={(currentPageValue as { page: 'retrofit/floorplan'; floorPlanId?: string }).floorPlanId} currentUser={currentUser} />;
      case 'retrofit/epc':
        return <EPCPage onNavigate={navigateToPage} floorPlanId={(currentPageValue as { page: 'retrofit/epc'; floorPlanId?: string }).floorPlanId} currentUser={currentUser} />;
      case 'retrofit/floor_plan_graph':
        return <FloorPlanGraphForm onNavigate={navigateToPage} floorPlanId={(currentPageValue as { page: 'retrofit/floor_plan_graph'; floorPlanId?: string }).floorPlanId} />;
      case 'retrofit/site_notes':
        return <AssessorSiteNotes onNavigate={navigateToPage} floorPlanId={(currentPageValue as { page: 'retrofit/site_notes'; floorPlanId?: string }).floorPlanId} />;
      case 'retrofit/cwi_ewi':
        return <CwiEwiPlan onNavigate={navigateToPage} floorPlanId={(currentPageValue as { page: 'retrofit/cwi_ewi'; floorPlanId?: string }).floorPlanId} />;
      case 'retrofit/iwi':
        return <IwiPlan onNavigate={navigateToPage} floorPlanId={(currentPageValue as { page: 'retrofit/iwi'; floorPlanId?: string }).floorPlanId} />;
      case 'retrofit/rir':
        return <RirPlan onNavigate={navigateToPage} floorPlanId={(currentPageValue as { page: 'retrofit/rir'; floorPlanId?: string }).floorPlanId} />;
      case 'retrofit/survey':
        return <RetrofitSurveyLayout onNavigate={navigateToPage} floorPlanId={(currentPageValue as { page: 'retrofit/survey'; floorPlanId?: string }).floorPlanId} />;
      case 'team-management':
        return <TeamManagementPage onNavigate={navigateToPage} currentUser={currentUser} />;
      case 'invite':
        return <InviteAcceptPage onNavigate={navigateToPage} currentUser={currentUser} invitationToken={(currentPageValue as { page: 'invite'; token?: string }).token} />;
      default:
        console.error("Reached default case in renderCurrentPage with an unexpected or unhandled page state:", (currentPageValue as any).page);
        // RoutingEffect should eventually correct an invalid page state.
        // Rendering LoadingScreen here is a safe fallback while effects run.
        return <LoadingScreen progress={100} />;
    }
  };

  console.log('[DEBUG] App: About to render', { 
    currentPage: getCurrentPageSafe().page, 
    isInitialLoadComplete, 
    currentUser: currentUser?.id || 'null' 
  });

  // Show expired modal immediately if subscription is expired
  if (currentUser && isSubscriptionExpired) {
    return (
      <div className="min-h-screen bg-gray-100">
        <SubscriptionExpiredModal
          isOpen={true}
          planName={userSubscription?.subscription_type ? 
            (userSubscription.subscription_type === 'pro_trial' ? 'Pro Trial' : 
             userSubscription.subscription_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())) 
            : 'your plan'}
          onLogout={handleLogout}
        />
      </div>
    );
  }

  // Show loading while checking subscription (prevents content flash)
  if (currentUser && isCheckingSubscription) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Verifying account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="h-full w-full">
        {renderCurrentPage()}
      </div>
      {/* Chat notification popup (only show when user is authenticated) */}
      {currentUser && <ChatNotificationPopup onNavigate={navigateToPage} />}
    </div>
  );
}

export default App;